import { supabase } from '@/integrations/supabase/client';
import { SetupFunnelService } from '@/services/analytics/SetupFunnelService';
import { UserService } from '@/services/users/UserService';
import { ConversationMessage, SetupAnalysisResult } from '@/types/agent/setup';
import type { PersistedSetupState, SetupResumeResult } from '@/types/agent/setup-recovery';

export interface UnfinishedSetup {
  id: number;
  elevenlabs_id: string | null;
  agent_id: string | null;
  el_agent_id: string | null;
  created_at: string;
  type: string | null;
  user_id: string | null;
  viewed: boolean;
  metadata: ConversationMetadata | null;
  conversation_details: { id: number; conversation_id: string; transcript: ConversationMessage[] } | null;
  /**
   * True when a derived setup was captured at the time, so resuming cannot
   * fail on expired transcript data. Lets the UI be honest about which setups
   * are certain to come back and which are a best effort.
   */
  hasPersistedState?: boolean;
}

/** The free-form JSON blob on a conversation row. */
type ConversationMetadata = Record<string, unknown>;

/** One row as it comes back from the unfinished-setups query. */
type RawSetupRow = Omit<UnfinishedSetup, "hasPersistedState">;

/** Where the persisted setup state lives inside `conversations.metadata`. */
const PERSISTED_KEY = "derivedSetup";

const readPersisted = (metadata: ConversationMetadata | null | undefined): PersistedSetupState | null => {
  const raw = metadata?.[PERSISTED_KEY];
  if (!raw || typeof raw !== "object") return null;
  const candidate = raw as Partial<PersistedSetupState>;
  if (candidate.version !== 1) return null;
  return candidate as PersistedSetupState;
};

export class SetupRecoveryService {
  /**
   * Get all unfinished setups for the current user
   */
  public static async getUnfinishedSetups(): Promise<UnfinishedSetup[]> {
    try {
      const {
        data: { user },
      } = await UserService.getUser();

      if (!user) {
        console.error("No authenticated user found");
        return [];
      }

      const { data: conversations, error } = await supabase
        .from("conversations")
        .select("*, conversation_details(id,conversation_id,transcript)")
        .eq("type", "setup")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching unfinished setups:", error);
        return [];
      }

      const unfinishedSetups: UnfinishedSetup[] = (conversations || [])
        .filter((conv: RawSetupRow) => {
          const metadata = conv.metadata || {};
          const hasCreatedAgent = metadata.createdAgentId != null;
          const isAbandoned = metadata.setupStatus === "abandoned";
          return !hasCreatedAgent && !isAbandoned;
        })
        .map((conv: RawSetupRow) => ({
          ...conv,
          hasPersistedState: readPersisted(conv.metadata)?.kind === "analysis",
        }));

      return unfinishedSetups;
    } catch (error) {
      console.error("Failed to get unfinished setups:", error);
      return [];
    }
  }

  /**
   * Capture the derived setup state AT THE TIME it is derived.
   *
   * This is the fix for the recovery flow's worst failure. Recovery used to
   * re-derive a setup from the conversation transcript on demand, so once that
   * transcript aged out the customer was told to start over and everything
   * they had said was lost. Writing the derived state down while we have it
   * removes that dependency entirely.
   *
   * Never throws and never surfaces an error: failing to save a safety net
   * must not break the thing the net is under.
   */
  public static async persistDerivedSetupState(
    conversationId: string | null | undefined,
    state: { analysis?: SetupAnalysisResult | null; transcript?: ConversationMessage[] | null },
  ): Promise<void> {
    if (!conversationId) return;

    try {
      const { data: existing, error: fetchError } = await supabase
        .from("conversations")
        .select("metadata")
        .eq("elevenlabs_id", conversationId)
        .maybeSingle();

      if (fetchError) {
        console.warn("[setup-recovery] could not read conversation metadata:", fetchError.message);
        return;
      }

      const existingMetadata = (existing?.metadata as ConversationMetadata | null) || {};
      const previous = readPersisted(existingMetadata);

      // Never downgrade a complete derived setup to a transcript-only one.
      if (previous?.kind === "analysis" && !state.analysis) return;

      const analysis = (state.analysis ?? previous?.analysis) as SetupAnalysisResult | undefined;
      const transcript = state.transcript ?? previous?.transcript ?? [];

      // Nothing worth writing down.
      if (!analysis && transcript.length === 0) return;

      const payload: PersistedSetupState = {
        version: 1,
        derivedAt: new Date().toISOString(),
        analysis: analysis as SetupAnalysisResult,
        transcript,
        kind: analysis ? "analysis" : "transcript-only",
      };

      const { error } = await supabase
        .from("conversations")
        .update({ metadata: { ...existingMetadata, [PERSISTED_KEY]: payload } })
        .eq("elevenlabs_id", conversationId);

      if (error) {
        console.warn("[setup-recovery] could not persist derived setup state:", error.message);
        return;
      }

      SetupFunnelService.stateCaptured(conversationId, payload.kind);
    } catch (error) {
      console.warn("[setup-recovery] persistDerivedSetupState failed:", error);
    }
  }

  /** Read the persisted derived state for a setup conversation, if any. */
  public static async readPersistedSetupState(
    conversationId: string,
  ): Promise<PersistedSetupState | null> {
    try {
      const { data, error } = await supabase
        .from("conversations")
        .select("metadata")
        .eq("elevenlabs_id", conversationId)
        .maybeSingle();

      if (error) return null;
      return readPersisted(data?.metadata as ConversationMetadata | null);
    } catch {
      return null;
    }
  }

  /**
   * Resume setup from an existing conversation.
   *
   * Persisted state first. Only when nothing complete was captured do we fall
   * back to re-deriving from the transcript — the path that fails once the
   * upstream conversation data has expired. When that fallback succeeds we
   * persist the result, so the same setup can never fail to recover twice.
   */
  public static async resumeSetupFromConversation(conversationId: string): Promise<SetupResumeResult> {
    const persisted = await SetupRecoveryService.readPersistedSetupState(conversationId);

    if (persisted?.kind === "analysis" && persisted.analysis) {
      SetupFunnelService.resumeAttempted(conversationId, "persisted", true);
      return {
        analysis: persisted.analysis,
        transcript: persisted.transcript ?? [],
        source: "persisted",
      };
    }

    try {
      const { data, error } = await supabase.functions.invoke("handle-conversation", {
        body: {
          action: "setup",
          data: {
            conversationId,
            // Hand over whatever we saved earlier, so a setup that never
            // reached analysis can still be recovered from what the customer
            // actually said rather than from upstream data that may be gone.
            messages: persisted?.transcript?.length ? persisted.transcript : undefined,
          },
        },
      });

      if (error) {
        throw new Error(`Setup analysis failed: ${error.message}`);
      }

      if (!data.success || !data.analysis) {
        throw new Error(data.message || "Setup analysis failed");
      }

      const result: SetupResumeResult = {
        analysis: data.analysis,
        transcript: data.transcript || persisted?.transcript || [],
        source: "derived",
      };

      // Never make the customer roll this dice twice.
      void SetupRecoveryService.persistDerivedSetupState(conversationId, {
        analysis: result.analysis,
        transcript: result.transcript,
      });

      SetupFunnelService.resumeAttempted(conversationId, "derived", true);
      return result;
    } catch (error) {
      SetupFunnelService.resumeAttempted(conversationId, "derived", false);
      console.error("Failed to resume setup from conversation:", error);
      throw error;
    }
  }

  /**
   * Mark an unfinished setup as abandoned (remove from list)
   */
  public static async markSetupAsAbandoned(conversationId: string): Promise<void> {
    try {
      const {
        data: { user },
      } = await UserService.getUser();

      if (!user) {
        throw new Error("No authenticated user found");
      }

      const { data: existingConversation, error: fetchError } = await supabase
        .from("conversations")
        .select("metadata")
        .eq("elevenlabs_id", conversationId)
        .single();

      if (fetchError) {
        console.error("Error fetching conversation:", fetchError);
        return;
      }

      const updatedMetadata = {
        ...(existingConversation?.metadata || {}),
        setupStatus: "abandoned",
        abandonedAt: new Date().toISOString(),
      };

      const { error } = await supabase.from("conversations").update({ metadata: updatedMetadata }).eq("elevenlabs_id", conversationId);

      if (error) {
        console.error("Error marking setup as abandoned:", error);
      } else {
        SetupFunnelService.abandoned(conversationId, "dismissed");
      }
    } catch (error) {
      console.error("Failed to mark setup as abandoned:", error);
    }
  }
}
